// Глобальный дефолт axios (main.js берёт его отсюда). Живёт рядом с
// классификацией, потому что от него она и зависит: запрос, которому timeout
// подняли ЯВНО выше дефолта, — заведомо долгая операция, и его таймаут значит
// «не успела одна операция», а не «у пользователя пропала связь».
export const DEFAULT_TIMEOUT_MS = 30000;

// Классификация транзиентных сетевых ошибок axios (обрыв соединения, таймаут,
// отмена) — их шлём в Rollbar как warning, а не error: это шум мобильных сетей,
// а не дефект приложения. Ошибки с HTTP-ответом сервера (4xx/5xx) и programming
// errors остаются error-уровнем — кроме 429 (isRateLimitedError ниже): отказ по
// лимиту частоты тоже не дефект.
const TRANSIENT_AXIOS_CODES = [
  "ERR_NETWORK",
  "ECONNABORTED",
  "ETIMEDOUT",
  "ERR_CANCELED",
];

export function isTransientNetworkError(error) {
  if (!error || !error.isAxiosError || error.response) {
    return false;
  }
  if (TRANSIENT_AXIOS_CODES.indexOf(error.code) !== -1) {
    return true;
  }
  const message = error.message || "";
  if (message === "Network Error" || /timeout of .+ exceeded/i.test(message)) {
    return true;
  }
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

// Коды, которыми axios сообщает именно об истечении таймаута (в отличие от
// обрыва соединения ERR_NETWORK и отмены ERR_CANCELED).
const TIMEOUT_CODES = ["ECONNABORTED", "ETIMEDOUT"];

// Таймаут долгой операции — НЕ «проблема с сетью».
//
// Инцидент 2026-08-19: распознавание документа в CRM ждало сервер, который
// качал файл из Green-API дольше 30 с. Клиент отваливался по глобальному
// таймауту, ответа сервера (и его причины отказа) не видел вовсе — и получал
// модалку «Проверьте соединение» при исправной сети, поверх тоста «Файл не
// распознан». Два сообщения, оба мимо.
//
// Признак — поднятый per-call timeout: его ставят осознанно там, где операция
// длинная (OCR, выгрузки шаблонов, экспорт парка). Такой запрос обязан
// объяснять свою неудачу сам, своим тостом; глобальный баннер о состоянии сети
// на нём молчит. Обрыв связи здесь ни при чём: он приходит как ERR_NETWORK и
// продолжает баннер взводить, независимо от величины timeout.
export function isLongOperationTimeout(error) {
  if (!error || !error.config) {
    return false;
  }
  const timeout = Number(error.config.timeout);
  if (!Number.isFinite(timeout) || timeout <= DEFAULT_TIMEOUT_MS) {
    return false;
  }
  if (TIMEOUT_CODES.indexOf(error.code) !== -1) {
    return true;
  }
  return /timeout of .+ exceeded/i.test(error.message || "");
}

// Политика тихого авторетрая (axios-retry): только безопасные методы —
// повтор POST при обрыве может продублировать запись (кассы, платежи).
// Таймауты не ретраим (каждый повтор ждал бы ещё до 30с), отмены — тем более.
const RETRY_SAFE_METHODS = ["get", "head", "options"];
const NO_RETRY_CODES = ["ECONNABORTED", "ETIMEDOUT", "ERR_CANCELED"];

export function shouldRetryRequest(error) {
  if (!error || !error.config) {
    return false;
  }
  const method = (error.config.method || "").toLowerCase();
  if (RETRY_SAFE_METHODS.indexOf(method) === -1) {
    return false;
  }
  if (NO_RETRY_CODES.indexOf(error.code) !== -1) {
    return false;
  }
  return isTransientNetworkError(error);
}

// Response-interceptor для глобального индикатора «Проблема с сетью» (App.vue):
// транзиентная сетевая ошибка взводит store-флаг, первый же ответ сервера его
// снимает. Именно ЛЮБОЙ ответ, а не только успешный: 500 — это плохая новость о
// сервере, но хорошая о связи. Иначе после обрыва и ретрая, ответившего 500,
// поверх страницы висела бы «Проблема с сетью» при исправной сети (находка
// финального ревью 21.09.2026).
export function createNetworkFlagInterceptor(store) {
  return {
    onSuccess(response) {
      if (store.state.network.problem) {
        store.commit("network/SET_PROBLEM", false);
      }
      return response;
    },
    onError(error) {
      if (isTransientNetworkError(error) && !isLongOperationTimeout(error)) {
        store.commit("network/SET_PROBLEM", true);
      } else if (error && error.response && store.state.network.problem) {
        store.commit("network/SET_PROBLEM", false);
      }
      return Promise.reject(error);
    },
  };
}

// 429 — сервер отказал по лимиту частоты запросов (Rack::Attack на IP, либо
// контроллерный rate_limit). Это не дефект и не проблема сети: запрос дошёл,
// сервер ответил осознанно. Инцидент 2026-09-29: офис «26 РЕГИОН» за одним NAT
// выбрал req/ip/5min, и карточка брони показывала красную «ошибку» с сырым
// «status code 429», а Rollbar копил её как error.
export function isRateLimitedError(error) {
  return !!(error && error.response && error.response.status === 429);
}

// Сколько ждать, в секундах. Rack::Attack и CRM-подсказки шлют заголовок
// Retry-After (браузеру он виден через CORS expose, rentalman-api cors.rb);
// контроллерные лимиты (AI-чат, CRM) кладут retry_after в тело. Заголовок важнее.
// Нет срока или в нём не число — null.
export function rateLimitRetrySeconds(error) {
  const response = error && error.response;
  if (!response) return null;
  const header = response.headers && response.headers["retry-after"];
  const body = response.data && typeof response.data === "object" ? response.data.retry_after : null;
  const raw = header || body;
  if (!raw && raw !== 0) return null;
  const seconds = Number(raw);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

// В минутах с округлением вверх (минимум 1): лимиты у нас 1 и 5 минут, секунды
// человеку не нужны, а округление вниз обещало бы слишком рано.
export function rateLimitRetryMinutes(error) {
  const seconds = rateLimitRetrySeconds(error);
  return seconds === null ? null : Math.max(1, Math.ceil(seconds / 60));
}

// Текст отказа по лимиту для человека — один на все обработчики ошибок
// (setErrorMixin, reportRentToOwnFailure, Signup, TaxiDriverInfo),
// чтобы формулировка и срок не разъехались. vm — любой объект с $t (компонент).
export function rateLimitedMessage(vm, error) {
  const minutes = rateLimitRetryMinutes(error);
  return minutes ? vm.$t("errors.rate_limited_retry", { minutes }) : vm.$t("errors.rate_limited");
}
